import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, Filter } from 'lucide-react';
import FilterableDataTable from '../../../components/ui/FilterableDataTable';
import SearchBar from '../../../components/ui/SearchBar';
import AnchorDropdownPanel from '../../../components/ui/AnchorDropdownPanel';
import DropdownSearch from '../../../components/ui/DropdownSearch';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import toast from '../../../services/toast';
import { dropdown, table, cn } from '../../../components/ui/uiTheme';
import { SectionSubtitle } from '../../../components/ui/PageLayout';
import { dashboardCommitsColumns } from '../dashboardActivityColumns';
import ServerTablePagination from '../ServerTablePagination';
import {
  commitSortToServer,
  isClientOnlyCommitSort,
  useDashboardCommitsQuery,
  useDashboardReposForFilter,
} from '../hooks/useDashboardActivityQueries';

function startOfLocalDayToIso(dateStr: string) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

function endOfLocalDayToIso(dateStr: string) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

function isoToDateInput(iso: string) {
  if (!iso || typeof iso !== 'string') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

const DEFAULT_COMMITS_SORT = [{ id: 'commit', desc: true }];

const COMMITS_SORT_OPTIONS = [
  { key: 'commit-desc', columnId: 'commit', desc: true, label: 'Date (newest first)' },
  { key: 'commit-asc', columnId: 'commit', desc: false, label: 'Date (oldest first)' },
  { key: 'files-desc', columnId: 'commitFiles', desc: true, label: 'Files changed (high → low)' },
  { key: 'files-asc', columnId: 'commitFiles', desc: false, label: 'Files changed (low → high)' },
  { key: 'churn-desc', columnId: 'addDelChurn', desc: true, label: 'Churn ratio (high → low)' },
  { key: 'churn-asc', columnId: 'addDelChurn', desc: false, label: 'Churn ratio (low → high)' },
  { key: 'add-desc', columnId: 'commitAdditions', desc: true, label: 'Additions (high → low)' },
  { key: 'add-asc', columnId: 'commitAdditions', desc: false, label: 'Additions (low → high)' },
  { key: 'del-desc', columnId: 'commitDeletions', desc: true, label: 'Deletions (high → low)' },
  { key: 'del-asc', columnId: 'commitDeletions', desc: false, label: 'Deletions (low → high)' },
];

const DEFAULT_LIMIT = 50;

export default function DashboardCommitsTab({
  orgId,
  active,
}: {
  orgId: string | undefined;
  active: boolean;
}) {
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [serverSort, setServerSort] = useState({ sortBy: 'authorDate', sortDir: 'desc' });
  const [commitsSearch, setCommitsSearch] = useState('');
  const [debouncedMessage, setDebouncedMessage] = useState('');
  const [commitsSort, setCommitsSort] = useState(DEFAULT_COMMITS_SORT);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filterAnchorRef = useRef<HTMLButtonElement>(null);
  const sortAnchorRef = useRef<HTMLButtonElement>(null);

  const [appliedFilters, setAppliedFilters] = useState({
    branch: '',
    author: '',
    from: '',
    to: '',
    repositoryId: '',
  });
  const [draftBranch, setDraftBranch] = useState('');
  const [draftAuthor, setDraftAuthor] = useState('');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [draftRepositoryId, setDraftRepositoryId] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedMessage(commitsSearch.trim()), 350);
    return () => window.clearTimeout(t);
  }, [commitsSearch]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedMessage, appliedFilters, serverSort, limit]);

  const queryParams = useMemo(
    () => ({
      limit,
      offset,
      sortBy: serverSort.sortBy,
      sortDir: serverSort.sortDir,
      ...(appliedFilters.branch ? { branch: appliedFilters.branch } : {}),
      ...(appliedFilters.author ? { author: appliedFilters.author } : {}),
      ...(appliedFilters.from ? { from: appliedFilters.from } : {}),
      ...(appliedFilters.to ? { to: appliedFilters.to } : {}),
      ...(appliedFilters.repositoryId ? { repositoryId: appliedFilters.repositoryId } : {}),
      ...(debouncedMessage ? { message: debouncedMessage } : {}),
    }),
    [limit, offset, serverSort, appliedFilters, debouncedMessage]
  );

  const commitsQuery = useDashboardCommitsQuery(orgId, active, queryParams);
  const reposQuery = useDashboardReposForFilter(orgId, active);

  const commits = commitsQuery.data?.commits ?? [];
  const meta = commitsQuery.data?.meta;
  const total = meta?.total ?? 0;
  const loading = commitsQuery.isPending;

  const repoOptions = useMemo(() => {
    const rows = reposQuery.data ?? [];
    return [
      { id: '', label: 'All repositories' },
      ...rows.map((r: any) => ({
        id: r.id,
        label: r.fullName || r.name || r.id,
      })),
    ];
  }, [reposQuery.data]);

  useEffect(() => {
    if (!filterOpen) return;
    setDraftBranch(appliedFilters.branch || '');
    setDraftAuthor(appliedFilters.author || '');
    setDraftFrom(isoToDateInput(appliedFilters.from));
    setDraftTo(isoToDateInput(appliedFilters.to));
    setDraftRepositoryId(appliedFilters.repositoryId || '');
  }, [filterOpen, appliedFilters]);

  const hasActiveCommitFilters = Boolean(
    appliedFilters.branch?.trim() ||
      appliedFilters.author?.trim() ||
      appliedFilters.from?.trim() ||
      appliedFilters.to?.trim() ||
      appliedFilters.repositoryId?.trim()
  );

  const handleApplyFilters = () => {
    if (draftFrom && draftTo && draftFrom > draftTo) {
      toast.error('Start date must be on or before the end date.');
      return;
    }
    setAppliedFilters({
      branch: draftBranch?.trim() || '',
      author: draftAuthor?.trim() || '',
      from: draftFrom ? startOfLocalDayToIso(draftFrom) : '',
      to: draftTo ? endOfLocalDayToIso(draftTo) : '',
      repositoryId: draftRepositoryId?.trim() || '',
    });
    setFilterOpen(false);
  };

  const handleClearAppliedFilters = () => {
    setAppliedFilters({ branch: '', author: '', from: '', to: '', repositoryId: '' });
    setDraftBranch('');
    setDraftAuthor('');
    setDraftFrom('');
    setDraftTo('');
    setDraftRepositoryId('');
    setFilterOpen(false);
  };

  const handleSortPick = (opt: (typeof COMMITS_SORT_OPTIONS)[number]) => {
    setCommitsSort([{ id: opt.columnId, desc: opt.desc }]);
    if (!isClientOnlyCommitSort(opt.columnId)) {
      setServerSort(commitSortToServer(opt.columnId, opt.desc));
    }
    setSortOpen(false);
  };

  return (
    <div role="tabpanel" id="dashboard-tabpanel-commits" aria-labelledby="dashboard-tab-commits">
      <FeatureGate flag="ui.dashboard.commitsTab">
        <div className={cn(table.wrapper, 'overflow-hidden')}>
          <div className="px-4 py-3 border-b border-dark-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1">
              <SectionSubtitle>Commits</SectionSubtitle>
              <p className="text-sm text-slate-400 mt-0.5">
                {total > 0 && <>Total: {total} · </>}
                Organisation-wide commits across assigned repositories.
              </p>
            </div>
            {!loading && (
              <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:max-w-xl md:max-w-2xl">
                <div className="min-w-0 flex-1 sm:min-w-[12rem] sm:max-w-md">
                  <SearchBar
                    value={commitsSearch}
                    onChange={setCommitsSearch}
                    placeholder="Search commit messages…"
                  />
                </div>
                <button
                  ref={sortAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setSortOpen((v) => !v)}
                  aria-label="Sort commits"
                  title="Sort commits"
                  aria-expanded={sortOpen}
                  aria-haspopup="dialog"
                  aria-controls="dashboard-commits-sort-menu"
                >
                  <ArrowUpDown className="h-5 w-5 text-slate-200" aria-hidden />
                </button>
                <button
                  ref={filterAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-label="Filter commits"
                  title="Filter commits"
                  aria-expanded={filterOpen}
                  aria-haspopup="dialog"
                  aria-controls="dashboard-commits-filter-menu"
                >
                  <span className="relative inline-flex">
                    <Filter className="h-5 w-5 text-slate-200" aria-hidden />
                    {hasActiveCommitFilters && (
                      <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-vivid-red ring-2 ring-dark-surface"
                        aria-hidden
                      />
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>

          <FilterableDataTable
            key={`dashboard-commits-${orgId}`}
            data={commits}
            columns={dashboardCommitsColumns}
            getRowId={(row) => String(row.id)}
            isLoading={loading}
            emptyMessage="No commits found."
            initialSorting={DEFAULT_COMMITS_SORT}
            sorting={commitsSort}
            onSortingChange={setCommitsSort}
            showColumnHeaders={false}
            hideTopSearchRow
            enablePagination={false}
          />

          <ServerTablePagination
            total={total}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            onLimitChange={(next) => {
              setLimit(next);
              setOffset(0);
            }}
            idPrefix="dashboard-commits"
          />

          <AnchorDropdownPanel
            open={sortOpen}
            onClose={() => setSortOpen(false)}
            anchorRef={sortAnchorRef}
            id="dashboard-commits-sort-menu"
            ariaLabel="Sort commits"
            panelWidth={300}
          >
            <div className="space-y-1" role="list">
              {COMMITS_SORT_OPTIONS.map((opt) => {
                const activeSort =
                  commitsSort.length === 1 &&
                  commitsSort[0].id === opt.columnId &&
                  commitsSort[0].desc === opt.desc;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="listitem"
                    className={cn(
                      'w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                      activeSort ? 'bg-brand-purple/15 text-brand-purple-light' : dropdown.item
                    )}
                    onClick={() => handleSortPick(opt)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </AnchorDropdownPanel>

          <AnchorDropdownPanel
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            anchorRef={filterAnchorRef}
            id="dashboard-commits-filter-menu"
            title="Filter commits"
            titleIcon={<Filter className="h-5 w-5 shrink-0 text-vivid-red" aria-hidden />}
            panelWidth={400}
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="dashboard-commits-filter-repo"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  Repository
                </label>
                <DropdownSearch
                  id="dashboard-commits-filter-repo"
                  options={repoOptions}
                  value={draftRepositoryId}
                  onChange={(opt) => setDraftRepositoryId(opt?.id ?? '')}
                  getOptionValue={(o) => o.id}
                  getOptionLabel={(o) => o.label}
                  placeholder="Search repositories…"
                  emptyMessage="No matching repository"
                  compact
                />
              </div>
              <div>
                <label
                  htmlFor="dashboard-commits-filter-branch"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  Branch
                </label>
                <input
                  id="dashboard-commits-filter-branch"
                  type="text"
                  value={draftBranch}
                  onChange={(e) => setDraftBranch(e.target.value)}
                  placeholder="Exact branch name"
                  className="input-mdl-sm w-full"
                />
              </div>
              <div>
                <label
                  htmlFor="dashboard-commits-filter-author"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  Author
                </label>
                <input
                  id="dashboard-commits-filter-author"
                  type="text"
                  value={draftAuthor}
                  onChange={(e) => setDraftAuthor(e.target.value)}
                  placeholder="Name, email, or GitHub handle"
                  className="input-mdl-sm w-full"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="dashboard-commits-filter-from"
                    className="block text-sm font-medium text-slate-300 mb-1.5"
                  >
                    Start date
                  </label>
                  <input
                    id="dashboard-commits-filter-from"
                    type="date"
                    value={draftFrom}
                    onChange={(e) => setDraftFrom(e.target.value)}
                    className="input-mdl-sm w-full"
                  />
                </div>
                <div>
                  <label
                    htmlFor="dashboard-commits-filter-to"
                    className="block text-sm font-medium text-slate-300 mb-1.5"
                  >
                    End date
                  </label>
                  <input
                    id="dashboard-commits-filter-to"
                    type="date"
                    value={draftTo}
                    onChange={(e) => setDraftTo(e.target.value)}
                    className="input-mdl-sm w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={() => setFilterOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={handleClearAppliedFilters}
                  disabled={
                    !hasActiveCommitFilters &&
                    !(draftBranch || draftAuthor || draftFrom || draftTo || draftRepositoryId)
                  }
                >
                  Clear filters
                </button>
                <button type="button" className="btn-primary-mdl-sm" onClick={handleApplyFilters}>
                  Apply
                </button>
              </div>
            </div>
          </AnchorDropdownPanel>
        </div>
      </FeatureGate>
    </div>
  );
}
